import React, { useState, useMemo } from "react";
import {
  useGetStagingTemplatesQuery,
  useGetProdTemplatesQuery,
  useGetSyncStatusQuery,
  useSyncStagingToProdMutation,
  useSyncProdToStagingMutation,
  useCreateStagingTemplateMutation,
  useCreateProdTemplateMutation,
} from "../../../app/api/console/scanTemplateSyncApiSlice";
import TemplateListItem from "./components/TemplateListItem";
import TemplateDetail from "./components/TemplateDetail";
import SyncConfirmDialog from "./components/SyncConfirmDialog";
import JustForYouPanel from "./JustForYou";

const SCAN_MODES = [
  { value: "scraper_one_look", label: "Scraper (One Look)" },
  { value: "agentic_test", label: "Agentic Test" },
  { value: "agentic_test_definition", label: "Discovery (Test Definition)" },
  { value: "broken_link", label: "Broken Link Checker" },
  { value: "broken_image", label: "Broken Image Checker" },
];

function ScanTemplatesV2() {
  // libraryMode: "templates" = V1 scan_templates_v2; "just_for_you" = Hub custom agents
  const [libraryMode, setLibraryMode] = useState("just_for_you");
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [environment, setEnvironment] = useState("staging");
  const [filterTypes, setFilterTypes] = useState(["base", "customized_base"]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [syncDialog, setSyncDialog] = useState(null);
  const [duplicateSource, setDuplicateSource] = useState(null);
  const [newTemplate, setNewTemplate] = useState({
    title: "", description: "", time_minutes: 60,
    plan_type: "core", automation_ready: false, scan_mode: "scraper_one_look",
    requires_file: false, requires_scope: false, show_test_cases: false, categories: [], tags: [],
    description_items: [], initial_prompt_text: "",
  });

  const toggleFilterType = (type) => {
    setFilterTypes((prev) => {
      if (prev.includes(type)) { if (prev.length === 1) return prev; return prev.filter((t) => t !== type); }
      return [...prev, type];
    });
  };

  const { data: stagingData, isLoading: isLoadingStaging } = useGetStagingTemplatesQuery({}, { skip: libraryMode !== "templates" });
  const { data: prodData, isLoading: isLoadingProd } = useGetProdTemplatesQuery({}, { skip: libraryMode !== "templates" });
  const { data: syncStatusList } = useGetSyncStatusQuery({}, { skip: libraryMode !== "templates" });

  const [syncToProd, { isLoading: isSyncingToProd }] = useSyncStagingToProdMutation();
  const [syncToStaging, { isLoading: isSyncingToStaging }] = useSyncProdToStagingMutation();
  const [createStagingTemplate, { isLoading: isCreatingStagingTemplate }] = useCreateStagingTemplateMutation();
  const [createProdTemplate, { isLoading: isCreatingProdTemplate }] = useCreateProdTemplateMutation();
  const isCreatingTemplate = environment === "staging" ? isCreatingStagingTemplate : isCreatingProdTemplate;

  const resetCreateForm = () => {
    setNewTemplate({ title: "", description: "", time_minutes: 60, plan_type: "core", automation_ready: false, scan_mode: "scraper_one_look", requires_file: false, requires_scope: false, show_test_cases: false, categories: [], tags: [], description_items: [], initial_prompt_text: "" });
    setDuplicateSource(null);
  };

  const handleCreateTemplate = async () => {
    const createFn = environment === "staging" ? createStagingTemplate : createProdTemplate;
    const result = await createFn(newTemplate);
    if (result.data) {
      setSelectedTemplateId(result.data.scan_template_id);
      setShowCreateModal(false);
      resetCreateForm();
    }
  };

  const handleDuplicate = (sourceTemplate) => {
    setNewTemplate({
      title: `${sourceTemplate.title} (Copy)`,
      description: sourceTemplate.description || "",
      time_minutes: sourceTemplate.time_minutes || 60,
      plan_type: sourceTemplate.plan_type || "core",
      automation_ready: sourceTemplate.automation_ready || false,
      scan_mode: sourceTemplate.scan_mode || "scraper_one_look",
      requires_file: sourceTemplate.requires_file ?? false,
      requires_scope: sourceTemplate.requires_scope ?? false,
      show_test_cases: sourceTemplate.show_test_cases ?? false,
      categories: sourceTemplate.categories || [],
      tags: sourceTemplate.tags || [],
      description_items: sourceTemplate.description_items || [],
      initial_prompt_text: sourceTemplate.base_prompt?.current_prompt_text || "",
    });
    setDuplicateSource(sourceTemplate.title);
    setShowCreateModal(true);
  };

  const handleSync = async (direction) => {
    if (!selectedTemplateId) return;
    if (direction === "to-prod") { await syncToProd({ templateIds: [selectedTemplateId] }); }
    else { await syncToStaging({ templateIds: [selectedTemplateId] }); }
    setSyncDialog(null);
  };

  const templates = useMemo(() => {
    const data = environment === "staging" ? stagingData : prodData;
    if (!data?.templates) return [];
    let filtered = [...data.templates];
    if (filterTypes.length < 3) { filtered = filtered.filter((t) => filterTypes.includes(t.template_type)); }
    if (searchQuery) { const q = searchQuery.toLowerCase(); filtered = filtered.filter((t) => t.title.toLowerCase().includes(q) || t.description?.toLowerCase().includes(q)); }
    filtered.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
    return filtered;
  }, [environment, stagingData, prodData, filterTypes, searchQuery]);

  const syncStatusMap = useMemo(() => {
    if (!syncStatusList) return {};
    return syncStatusList.reduce((acc, s) => { acc[s.template_id] = s; return acc; }, {});
  }, [syncStatusList]);

  const selectedSyncStatus = selectedTemplateId ? syncStatusMap[selectedTemplateId] : null;
  const isLoading = isLoadingStaging || isLoadingProd;

  return (
    <div className="flex flex-col" style={{ height: "calc(100vh - 7rem)" }}>
      <div className="mb-5">
        <h1 className="text-h2 text-text-primary">Agent Library</h1>
        <p className="text-body-sm text-text-secondary mt-1">
          {libraryMode === "just_for_you"
            ? "Hub “Just for you” custom agents — library cards, skills, subskills, and frontmatter"
            : "Manage scan templates with versioned prompts"}
        </p>
      </div>

      <div className="flex items-center gap-3 flex-wrap mb-4">
        <div className="flex rounded-xl overflow-hidden border border-border-subtle">
          <button
            onClick={() => setLibraryMode("just_for_you")}
            className={`px-4 py-2 text-body-xs font-semibold transition-colors ${
              libraryMode === "just_for_you"
                ? "bg-amber-500 text-white"
                : "bg-white text-text-secondary hover:bg-surface-neutral"
            }`}
          >
            Just for you
          </button>
          <button
            onClick={() => setLibraryMode("templates")}
            className={`px-4 py-2 text-body-xs font-semibold transition-colors ${
              libraryMode === "templates"
                ? "bg-button-primary text-white"
                : "bg-white text-text-secondary hover:bg-surface-neutral"
            }`}
          >
            Scan templates
          </button>
        </div>

        <div className="flex rounded-xl overflow-hidden border border-border-subtle">
          <button onClick={() => setEnvironment("staging")} className={`px-4 py-2 text-body-xs font-semibold transition-colors ${environment === "staging" ? "bg-button-primary text-white" : "bg-white text-text-secondary hover:bg-surface-neutral"}`}>Staging</button>
          <button onClick={() => setEnvironment("prod")} className={`px-4 py-2 text-body-xs font-semibold transition-colors ${environment === "prod" ? "bg-button-primary text-white" : "bg-white text-text-secondary hover:bg-surface-neutral"}`}>Production</button>
        </div>

        {libraryMode === "templates" && (
          <>
            <div className="flex items-center gap-0.5 rounded-xl overflow-hidden border border-border-subtle">
              <button onClick={() => setFilterTypes(["base", "custom", "customized_base"])} className={`px-3 py-2 text-body-xs font-medium transition-colors ${filterTypes.length === 3 ? "bg-button-primary text-white" : "bg-white text-text-secondary hover:bg-surface-neutral"}`}>All</button>
              <button onClick={() => toggleFilterType("base")} className={`px-3 py-2 text-body-xs font-medium transition-colors ${filterTypes.includes("base") ? "bg-accent-green/10 text-accent-green" : "bg-white text-text-secondary hover:bg-surface-neutral"}`}>Base</button>
              <button onClick={() => toggleFilterType("customized_base")} className={`px-3 py-2 text-body-xs font-medium transition-colors ${filterTypes.includes("customized_base") ? "bg-accent-purple/10 text-accent-purple" : "bg-white text-text-secondary hover:bg-surface-neutral"}`}>Customized</button>
              <button onClick={() => toggleFilterType("custom")} className={`px-3 py-2 text-body-xs font-medium transition-colors ${filterTypes.includes("custom") ? "bg-severity-medium/10 text-severity-medium" : "bg-white text-text-secondary hover:bg-surface-neutral"}`}>Custom</button>
            </div>

            <div className="relative flex-1 max-w-[200px]">
              <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-tertiary pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input type="text" placeholder="Search..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full pl-8 pr-3 py-2 text-body-xs bg-white rounded-xl border border-border-default focus:outline-none focus:ring-2 focus:ring-accent-purple/20 focus:border-accent-purple/40 placeholder:text-text-tertiary" />
            </div>

            <button onClick={() => setShowCreateModal(true)} className="px-4 py-2 bg-button-primary text-white rounded-xl text-body-xs font-semibold hover:bg-button-primary-hover transition-colors flex items-center gap-1.5">
              <span>+</span> New Template
            </button>

            {selectedTemplateId && (
              <div className="flex gap-2 ml-auto">
                <button
                  onClick={() => setSyncDialog("to-prod")}
                  disabled={isSyncingToProd || !selectedSyncStatus?.staging_exists}
                  className="px-3.5 py-2 bg-accent-green text-white rounded-xl text-body-xs font-semibold hover:opacity-90 disabled:opacity-40 transition-all"
                >
                  Push to Prod
                </button>
                <button
                  onClick={() => setSyncDialog("to-staging")}
                  disabled={isSyncingToStaging || !selectedSyncStatus?.prod_exists}
                  className="px-3.5 py-2 bg-accent-purple text-white rounded-xl text-body-xs font-semibold hover:opacity-90 disabled:opacity-40 transition-all"
                >
                  Pull to Staging
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {libraryMode === "just_for_you" ? (
        <JustForYouPanel environment={environment} />
      ) : (
        <>
          <div className="flex-1 flex overflow-hidden rounded-2xl border border-border-subtle bg-white">
            <div className="w-72 min-w-[288px] border-r border-border-subtle overflow-y-auto scrollbar-thin">
              {isLoading ? (
                <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-5 w-5 border-2 border-accent-purple border-t-transparent" /></div>
              ) : templates.length === 0 ? (
                <div className="p-6 text-center text-body-sm text-text-tertiary">No templates found</div>
              ) : (
                templates.map((template) => (
                  <TemplateListItem
                    key={template.scan_template_id}
                    template={template}
                    syncStatus={syncStatusMap[template.scan_template_id]}
                    isSelected={selectedTemplateId === template.scan_template_id}
                    onClick={() => setSelectedTemplateId(template.scan_template_id)}
                  />
                ))
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {selectedTemplateId ? (
                <TemplateDetail templateId={selectedTemplateId} environment={environment} onDeleted={() => setSelectedTemplateId(null)} onDuplicate={handleDuplicate} syncStatus={selectedSyncStatus} />
              ) : (
                <div className="flex items-center justify-center h-full text-body-sm text-text-tertiary">Select a template to view details</div>
              )}
            </div>
          </div>

          {syncDialog && selectedTemplateId && (
            <SyncConfirmDialog
              templateId={selectedTemplateId}
              direction={syncDialog}
              onConfirm={() => handleSync(syncDialog)}
              onCancel={() => setSyncDialog(null)}
              isSyncing={syncDialog === "to-prod" ? isSyncingToProd : isSyncingToStaging}
            />
          )}

          {showCreateModal && (
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
              <div className="bg-white rounded-2xl p-6 max-w-2xl w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto border border-border-subtle">
                <div className="flex items-center gap-3 mb-4">
                  <h3 className="text-body-lg font-semibold text-text-primary">
                    {duplicateSource ? "Duplicate Template" : "Create New Template"}
                  </h3>
                  {duplicateSource && (
                    <span className="px-2.5 py-1 rounded-lg text-xs font-medium bg-violet-100 text-violet-700 flex items-center gap-1.5">
                      From: {duplicateSource}
                    </span>
                  )}
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-body-xs font-medium text-text-secondary mb-1">Title *</label>
                    <input type="text" value={newTemplate.title} onChange={(e) => setNewTemplate({ ...newTemplate, title: e.target.value })} className="w-full px-3 py-2.5 border border-border-subtle rounded-xl text-body-sm focus:outline-none focus:ring-2 focus:ring-accent-purple/20 focus:border-accent-purple/40" placeholder="Template title" />
                  </div>
                  <div>
                    <label className="block text-body-xs font-medium text-text-secondary mb-1">Description</label>
                    <textarea value={newTemplate.description} onChange={(e) => setNewTemplate({ ...newTemplate, description: e.target.value })} className="w-full px-3 py-2.5 border border-border-subtle rounded-xl text-body-sm focus:outline-none focus:ring-2 focus:ring-accent-purple/20 focus:border-accent-purple/40" rows={3} placeholder="Template description" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-body-xs font-medium text-text-secondary mb-1">Scan Mode</label>
                      <select value={newTemplate.scan_mode} onChange={(e) => setNewTemplate({ ...newTemplate, scan_mode: e.target.value })} className="w-full px-3 py-2.5 border border-border-subtle rounded-xl text-body-sm focus:outline-none focus:ring-2 focus:ring-accent-purple/20">
                        {SCAN_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-body-xs font-medium text-text-secondary mb-1">Plan Type</label>
                      <select value={newTemplate.plan_type} onChange={(e) => setNewTemplate({ ...newTemplate, plan_type: e.target.value })} className="w-full px-3 py-2.5 border border-border-subtle rounded-xl text-body-sm focus:outline-none focus:ring-2 focus:ring-accent-purple/20">
                        <option value="core">CORE</option>
                        <option value="premium">PREMIUM</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-body-xs font-medium text-text-secondary mb-1">Time (minutes)</label>
                      <input type="number" value={newTemplate.time_minutes} onChange={(e) => setNewTemplate({ ...newTemplate, time_minutes: parseInt(e.target.value) || 60 })} className="w-full px-3 py-2.5 border border-border-subtle rounded-xl text-body-sm focus:outline-none focus:ring-2 focus:ring-accent-purple/20" />
                    </div>
                  </div>
                  <div className="flex gap-4 flex-wrap">
                    <label className="flex items-center gap-2"><input type="checkbox" checked={newTemplate.automation_ready} onChange={(e) => setNewTemplate({ ...newTemplate, automation_ready: e.target.checked })} className="rounded border-border-default text-accent-purple focus:ring-accent-purple/30" /><span className="text-body-xs font-medium text-text-secondary">Automation Ready</span></label>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={newTemplate.requires_file} onChange={(e) => setNewTemplate({ ...newTemplate, requires_file: e.target.checked })} className="rounded border-border-default text-accent-purple focus:ring-accent-purple/30" /><span className="text-body-xs font-medium text-text-secondary">Requires File</span></label>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={newTemplate.requires_scope} onChange={(e) => setNewTemplate({ ...newTemplate, requires_scope: e.target.checked })} className="rounded border-border-default text-accent-purple focus:ring-accent-purple/30" /><span className="text-body-xs font-medium text-text-secondary">Requires Scope</span></label>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={newTemplate.show_test_cases} onChange={(e) => setNewTemplate({ ...newTemplate, show_test_cases: e.target.checked })} className="rounded border-border-default text-accent-purple focus:ring-accent-purple/30" /><span className="text-body-xs font-medium text-text-secondary">Show Test Cases</span></label>
                  </div>
                  {newTemplate.initial_prompt_text && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Base Prompt</label>
                      <textarea
                        value={newTemplate.initial_prompt_text}
                        onChange={(e) => setNewTemplate({ ...newTemplate, initial_prompt_text: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg text-sm font-mono"
                        rows={6}
                      />
                    </div>
                  )}
                </div>
                <div className="flex gap-3 justify-end mt-6">
                  <button onClick={() => { setShowCreateModal(false); resetCreateForm(); }} className="px-4 py-2 border border-border-default rounded-xl text-body-sm text-text-secondary hover:bg-surface-neutral transition-colors">Cancel</button>
                  <button onClick={handleCreateTemplate} disabled={isCreatingTemplate || !newTemplate.title.trim()} className="px-4 py-2 bg-button-primary text-white rounded-xl text-body-sm font-semibold hover:bg-button-primary-hover disabled:opacity-40 transition-colors">
                    {isCreatingTemplate ? "Creating..." : duplicateSource ? "Create Copy" : "Create Template"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ScanTemplatesV2;
